/**
 * StandTypeAgaclari.jsx - the Stand Türü pickers' two-tree layout (backlog #596).
 *
 * stand_type holds two trees: the new one (names end in " (n)") and the legacy one. A root-level list mixes them,
 * so the pickers show the new types first in their normal colour and the legacy types after them, faded:
 *  - <StandTypeRows>    button pickers (Stand Yarat, Proje Kaydı Oluştur): two rows, each with a small caption;
 *  - <StandTypeOptions> select pickers (Proje Bilgilerini Düzenle, Fuar Kuralları, Belge Kuralları): two optgroups,
 *                       the legacy options greyed.
 * A list from one tree only (every level below the root) renders as before - no captions, no fading. Both trees stay
 * selectable and the " (n)" stays on the labels. Which tree a type belongs to: isNewTreeStandType (StandPills.jsx).
 */
import React from "react";
import { ButtonGroup } from "reactstrap";
import { useTranslation } from "react-i18next";
import { splitStandTypesByTree } from "./StandPills";

/**
 * `options`: the catalogue rows. `renderOption(type, faded)` returns the button (the caller owns its colour and
 * selected state; `faded` is true for a legacy row's button). `grouped` puts each row in a ButtonGroup.
 */
export function StandTypeRows({ options, renderOption, grouped = false }) {
  const { t } = useTranslation("projeler");
  const { fresh, legacy } = splitStandTypesByTree(options);
  const row = (list, faded) => (grouped
    ? <ButtonGroup size="sm" className="flex-wrap">{list.map((o) => renderOption(o, faded))}</ButtonGroup>
    : list.map((o) => renderOption(o, faded)));
  if (fresh.length === 0 || legacy.length === 0) {
    return <div className="d-flex flex-wrap align-items-center gap-2">{row(options || [], false)}</div>;
  }
  const caption = (text) => <span className="small text-muted" style={{ minWidth: "7.5rem" }}>{text}</span>;
  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex flex-wrap align-items-center gap-2" data-testid="stand-turu-yeni">
        {caption(t("standTipiAgaci.yeni"))}{row(fresh, false)}
      </div>
      <div className="d-flex flex-wrap align-items-center gap-2" data-testid="stand-turu-eski">
        {caption(t("standTipiAgaci.eski"))}{row(legacy, true)}
      </div>
    </div>
  );
}

/** `<option>`s of a select picker for `options` (use inside the <Input type="select">, after the empty option). */
export function StandTypeOptions({ options }) {
  const { t } = useTranslation("projeler");
  const { fresh, legacy } = splitStandTypesByTree(options);
  const opt = (o, faded) => (
    <option key={o.stand_type_id} value={o.stand_type_id} className={faded ? "text-muted" : undefined}>{o.stand_type_name}</option>
  );
  if (fresh.length === 0 || legacy.length === 0) return <>{(options || []).map((o) => opt(o, false))}</>;
  return (
    <>
      <optgroup label={t("standTipiAgaci.yeni")}>{fresh.map((o) => opt(o, false))}</optgroup>
      <optgroup label={t("standTipiAgaci.eski")}>{legacy.map((o) => opt(o, true))}</optgroup>
    </>
  );
}
